import { DAP } from "@gen/dap/v1/dap_pb";
import theme from "@theme";
import { Button, Heading, Icon, Modal, Tooltip } from "@thetreep/ui";
import { useTranslation } from "react-i18next";
import { Control, UseFormWatch, useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import i18n from "@libs/i18n";
import PassengerForm from "./PassengerForm";
import { Passenger, SearchRequest } from "@services/travel";
import {
  CardType,
  DisabilityType,
  WheelchairType,
} from "@gen/travel/v1/catalog_pb";
import { Gender } from "@gen/travel/v1/common_pb";
import { COMPANION_VALUE_UNSET } from "./usePassengerCompanionLogic";

export interface AdditionalPassengerFormProps {
  dap: DAP;
  cardTypes: CardType[];
  wheelchairTypes: WheelchairType[];
  disabilityTypes: DisabilityType[];
  control: Control<SearchRequest>;
  canBeACompanion: (
    passenger?: "beneficiary" | "additionalPassenger1" | "additionalPassenger2",
  ) => boolean;
  onAdditionalPassengerFormSubmit: ({
    passenger,
    otherPassengerIndex,
  }: {
    passenger: Passenger;
    otherPassengerIndex: number;
  }) => void;
  numberOfCompanions: number;
  passengers: Passenger[];
  append: (passenger: Passenger) => void;
  remove: (index: number) => void;
  update: (index: number, passenger: Passenger) => void;
}

export function AdditionalPassengerForm({
  dap,
  cardTypes,
  wheelchairTypes,
  disabilityTypes,
  canBeACompanion,
  onAdditionalPassengerFormSubmit,
  numberOfCompanions,
  passengers,
  append,
  remove,
  update,
}: AdditionalPassengerFormProps) {
  const { t } = useTranslation();

  const passengerForm = useForm<Passenger>({
    mode: "onTouched",
    defaultValues: {
      gender: Gender.MALE,
    },
  });

  const [isAddPassengerOpen, setIsAddPassengerOpen] = useState(false);

  function hasMaxCompanions(): boolean {
    return !dap.companions || passengers.length >= dap.companions;
  }

  function normalizePassenger(passenger: Passenger): Passenger {
    let phoneNumber = passenger.phoneNumber;
    if (phoneNumber && phoneNumber.length <= 4) {
      phoneNumber = "";
    }
    return {
      ...passenger,
      phoneNumber,
    };
  }

  async function handleSubmit() {
    const valid = await passengerForm.trigger();
    if (!valid) {
      return;
    }
    const passenger = passengerForm.getValues();
    append(normalizePassenger(passenger));
    passengerForm.reset();
    setIsAddPassengerOpen(false);
    onAdditionalPassengerFormSubmit({
      passenger,
      otherPassengerIndex: 0,
    });
  }

  async function handleEdit(index: number, passenger: Passenger) {
    update(index, normalizePassenger(passenger));
    onAdditionalPassengerFormSubmit({
      passenger,
      otherPassengerIndex: passengers.findIndex((_, idx) => index !== idx),
    });
  }

  function handleAddNewPassenger() {
    setIsAddPassengerOpen(true);
    if (canBeACompanion() && numberOfCompanions === 0) {
      passengerForm.setValue("hasCompanionFare", COMPANION_VALUE_UNSET);
    }
  }

  if (!dap.companions) {
    return;
  }

  return (
    <>
      <div
        className="
            w-full
            bg-neutral-50 p-8 rounded-md
            flex flex-col gap-4"
      >
        <Heading>{t("search.additionalPassengers.companions")}</Heading>
        {passengers.length > 0 && (
          <div className="flex items-center gap-2 text-grey-blue-500 text-xs">
            <Icon
              src="information_line"
              size={16}
              color={theme.colors["grey-blue"][500]}
            />
            <span>{t("search.additionalPassengers.info")}</span>
          </div>
        )}
        <div
          className={`border-t border-light-blue-200 ${passengers.length > 0 ? "mb-2 mt-4" : "my-2"}`}
        ></div>
        <div
          className={`grid gap-6 ${
            passengers.length > 0 ? "md:grid-cols-3" : "md:grid-cols-2"
          }`}
        >
          {passengers.map((passenger, index) => (
            <PassengerCard
              key={index}
              passenger={passenger}
              dap={dap}
              cardTypes={cardTypes}
              wheelchairTypes={wheelchairTypes}
              disabilityTypes={disabilityTypes}
              onEdit={(p) => handleEdit(index, p)}
              onDelete={() => remove(index)}
              canBeACompanion={canBeACompanion(
                index === 0 ? "additionalPassenger1" : "additionalPassenger2",
              )}
            />
          ))}
          <Tooltip
            bg={theme.colors.neutral[900]}
            color={theme.colors.neutral[50]}
            content={
              hasMaxCompanions() &&
              t("search.additionalPassengers.maxCompanionTooltip", {
                number: dap.companions,
              })
            }
          >
            <button
              data-testid="search-additionalpassenger-button"
              type="button"
              onClick={handleAddNewPassenger}
              disabled={hasMaxCompanions()}
              className="
                    h-[180px] w-full
                    border border-cnam-blue border-dashed rounded-md
                    flex items-center justify-center
                    hover:bg-cnam-light-100 active:bg-cnam-light-200
                    disabled:opacity-50 disabled:cursor-not-allowed
                    "
            >
              <div className="flex flex-col items-center gap-2">
                <div className="flex items-center gap-2">
                  <Icon
                    src="add_line"
                    size={18}
                    color={theme.colors.cnam.blue.DEFAULT}
                  />
                  <span className="text-cnam-blue font-bold">
                    {t("search.additionalPassengers.addCompanion")}
                  </span>
                </div>
                <span className="text-cnam-blue">
                  {t("search.additionalPassengers.maxCompanion", {
                    number: dap.companions,
                  })}
                </span>
              </div>
            </button>
          </Tooltip>
        </div>
        <PassengerModal
          isOpen={isAddPassengerOpen}
          isEditing={false}
          control={passengerForm.control}
          watch={passengerForm.watch}
          dap={dap}
          canBeACompanion={canBeACompanion()}
          cardTypes={cardTypes}
          wheelchairTypes={wheelchairTypes}
          disabilityTypes={disabilityTypes}
          onClose={() => setIsAddPassengerOpen(false)}
          onSubmit={handleSubmit}
        />
      </div>
    </>
  );
}

interface PassengerCardProps {
  passenger: Passenger;
  dap: DAP;
  cardTypes: CardType[];
  wheelchairTypes: WheelchairType[];
  disabilityTypes: DisabilityType[];
  onEdit: (p: Passenger) => void;
  onDelete: () => void;
  canBeACompanion: boolean;
}

function PassengerCard({
  passenger,
  dap,
  cardTypes,
  wheelchairTypes,
  disabilityTypes,
  onEdit,
  onDelete,
  canBeACompanion,
}: PassengerCardProps) {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const passengerForm = useForm<Passenger>({
    mode: "onTouched",
    defaultValues: passenger,
  });

  // The form is localized to this component, but we update the passenger from outside, this effect syncs both states
  useEffect(() => {
    passengerForm.setValue("hasCompanionFare", passenger.hasCompanionFare);
  }, [passenger.hasCompanionFare]);

  function handleSubmit() {
    const valid = passengerForm.trigger();
    if (!valid) {
      return;
    }
    onEdit(passengerForm.getValues());
    setIsEditing(false);
  }

  return (
    <>
      <div
        className="border border-cnam-light-300 rounded-md p-4 h-[180px] 
            flex flex-col justify-between"
      >
        <div className="flex justify-between">
          <div className="flex gap-2">
            <Icon src="group_line" color={theme.colors.cnam.bright} size={24} />
            <span className="font-jakarta font-bold">
              {passenger.firstName} {passenger.lastName}
            </span>
          </div>
          <button type="button" onClick={() => setIsEditing(true)}>
            <Icon src="pencil_line" color="black" />
          </button>
        </div>
        <span className="text-sm text-text-strong">
          {new Date(passenger.birthDate).toLocaleDateString(i18n.language)}
        </span>
        <div className="flex justify-between">
          <div>
            {passenger.disability && (
              <div className="flex gap-2 items-center">
                <Icon
                  src="PMR"
                  color={theme.colors.cnam.blue.DEFAULT}
                  size={16}
                />
                <span className="text-cnam-blue text-sm">
                  {t("disability")}
                </span>
              </div>
            )}
            {passenger.cards &&
              passenger.cards.length > 0 &&
              passenger.cards.map((c, i) => (
                <div className="flex gap-2 items-center" key={i}>
                  <Icon
                    src="coupon_line"
                    color={theme.colors.cnam.blue.DEFAULT}
                    size={16}
                  />
                  <span className="text-cnam-blue text-sm">
                    {cardTypes.find((c2) => c2.code === c.type)?.name || c.type}
                  </span>
                </div>
              ))}
          </div>
          <button type="button" className="self-end" onClick={onDelete}>
            <Icon src="trash" color="black" />
          </button>
        </div>
      </div>
      <PassengerModal
        isOpen={isEditing}
        isEditing={isEditing}
        control={passengerForm.control}
        dap={dap}
        watch={passengerForm.watch}
        cardTypes={cardTypes}
        wheelchairTypes={wheelchairTypes}
        disabilityTypes={disabilityTypes}
        onClose={() => setIsEditing(false)}
        onSubmit={handleSubmit}
        canBeACompanion={canBeACompanion}
      />
    </>
  );
}

interface PassengerModalProps {
  isOpen: boolean;
  isEditing: boolean;
  control: Control<Passenger>;
  dap: DAP;
  cardTypes: CardType[];
  wheelchairTypes: WheelchairType[];
  disabilityTypes: DisabilityType[];
  onClose: () => void;
  onSubmit: () => void;
  watch?: UseFormWatch<Passenger>;
  canBeACompanion: boolean;
}

function PassengerModal({
  isOpen,
  isEditing,
  control,
  dap,
  cardTypes,
  wheelchairTypes,
  disabilityTypes,
  onClose,
  onSubmit,
  watch,
  canBeACompanion,
}: PassengerModalProps) {
  const { t } = useTranslation();

  const watchedValues = watch?.();
  const isFormFilled =
    watchedValues?.firstName &&
    watchedValues?.lastName &&
    watchedValues?.birthDate;
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="auto"
      content={{
        title: isEditing
          ? t("search.additionalPassengers.editCompanion")
          : t("search.additionalPassengers.addCompanion"),
      }}
    >
      <div>
        <PassengerForm
          dap={dap}
          cardTypes={cardTypes}
          wheelchairTypes={wheelchairTypes}
          disabilityTypes={disabilityTypes}
          control={control}
          additionalPassenger
          canBeACompanion={canBeACompanion}
        />
        <div className="flex items-center justify-end gap-2">
          <Button
            size="compact"
            kind="outline"
            colors={{
              backgroundColor: theme.colors.cnam.blue.DEFAULT,
              color: theme.colors.cnam.blue.DEFAULT,
            }}
            onClick={onClose}
          >
            {t("cancel")}
          </Button>
          <Button
            data-testid="search-additionalpassenger-submit"
            size="compact"
            onClick={onSubmit}
            disabled={!isFormFilled}
          >
            {t("confirm")}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
